import { type AppOption } from '../../../../common/utils/app-filter-options';

export type AppPillLayout = {
	firstConnectedBatchSize: number;
	unauthenticated3pSlots: number;
	secondConnectedBatchSize: number;
	/**
	 * Hard cap on the total number of visible pills, applied after the batches above
	 * have been composed. Lets a caller express "fill the visible slots with connected
	 * apps first, then recommended unconnected 3P apps, but never show more than N" —
	 * which is how the refreshed FPS filter row configures its app tile count
	 * (QS-10724). Omit it to keep the batch sizes as the only budget.
	 */
	maxVisibleApps?: number;
};

// 6 connected 1P/3P apps then 3 top recommended unconnected 3P apps.
// Any remaining apps overflow to the "More apps" dropdown.
export const DEFAULT_APP_PILL_LAYOUT: AppPillLayout = {
	firstConnectedBatchSize: 6,
	unauthenticated3pSlots: 3,
	secondConnectedBatchSize: 0,
};

/**
 * Applies an {@link AppPillLayout} to the given authenticated/unauthenticated app option lists,
 * producing the visible pill order and the overflow remainder.
 *
 * Pure function (no React/feature-gate access) so it can be tested and reused independently.
 */
export const applyAppPillLayout = ({
	layout,
	authenticatedOptions,
	rankedUnauthenticated3p,
}: {
	layout: AppPillLayout;
	authenticatedOptions: AppOption[];
	rankedUnauthenticated3p: AppOption[];
}): { visibleOptions: AppOption[]; overflowOptions: AppOption[] } => {
	const {
		firstConnectedBatchSize,
		unauthenticated3pSlots,
		secondConnectedBatchSize,
		maxVisibleApps,
	} = layout;

	const firstConnectedBatch = authenticatedOptions.slice(0, firstConnectedBatchSize);
	const visibleUnauthenticated3p = rankedUnauthenticated3p.slice(0, unauthenticated3pSlots);
	const secondConnectedBatch = authenticatedOptions.slice(
		firstConnectedBatchSize,
		firstConnectedBatchSize + secondConnectedBatchSize,
	);
	const visibleOptions = [
		...firstConnectedBatch,
		...visibleUnauthenticated3p,
		...secondConnectedBatch,
	];

	const overflowAuthenticated = authenticatedOptions.slice(
		firstConnectedBatchSize + secondConnectedBatchSize,
	);
	const overflowUnauthenticated3p = rankedUnauthenticated3p.slice(unauthenticated3pSlots);
	const overflowOptions = [...overflowAuthenticated, ...overflowUnauthenticated3p];

	if (maxVisibleApps === undefined || visibleOptions.length <= maxVisibleApps) {
		return { visibleOptions, overflowOptions };
	}

	// Anything trimmed by the cap belongs in the overflow list, still grouped
	// connected-then-unconnected so the overflow list reads in the same order it
	// would have without the cap.
	const trimmed = visibleOptions.slice(maxVisibleApps);
	const trimmedAuthenticated = trimmed.filter((option) => !option.isUnauthenticated);
	const trimmedUnauthenticated = trimmed.filter((option) => option.isUnauthenticated);

	return {
		visibleOptions: visibleOptions.slice(0, maxVisibleApps),
		overflowOptions: [
			...trimmedAuthenticated,
			...overflowAuthenticated,
			...trimmedUnauthenticated,
			...overflowUnauthenticated3p,
		],
	};
};
